import {
    CalendarOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    DownloadOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert, Button, Card, Col, Progress, Row, Space, Statistic, Steps, Table, Tag, Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;
const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };

export default function RepriseGumVtDates() {
    const [step, setStep]         = useState(0);   // 0 sélection, 1 aperçu, 2 application, 3 rapport
    const [fichier, setFichier]   = useState(null);
    const [apercu, setApercu]     = useState(null);
    const [rapport, setRapport]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [progress, setProgress] = useState(0);
    const [jobId, setJobId]       = useState(null);
    const [jobEtat, setJobEtat]   = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const previsualiser = async () => {
        if (!fichier) { toast.warning("Sélectionnez le fichier Excel."); return; }
        setLoading(true); setApercu(null);
        try {
            const form = new FormData(); form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/corriger-dates/preview", form,
                { headers: { "Content-Type": "multipart/form-data" } });
            setApercu(res.data); setStep(1);
        } catch (err) {
            toast.error("Erreur aperçu : " + (err.response?.data?.message || err.message));
        } finally { setLoading(false); }
    };

    const appliquer = async () => {
        if (!fichier) return;
        setLoading(true); setRapport(null); setProgress(5); setJobEtat("EN_COURS"); setStep(2);
        try {
            const form = new FormData(); form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/corriger-dates", form,
                { headers: { "Content-Type": "multipart/form-data" } });
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id); demarrerPolling(id);
        } catch (err) {
            setLoading(false); setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100); setJobEtat(statut.etat); setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat); setStep(3);
                        toast.success("Correction des dates terminée !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current); setLoading(false); setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = (d) => {
        if (!d) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Lignes traitées (col. D renseignée)", d.totalLignes],
            ["Décaissements corrigés", d.corriges],
            ["Introuvables (n° règlement)", d.nonTrouves],
            ["Lignes ignorées (col. D vide)", d.ignoresDVide],
            ["Lignes col. Q vide (date règlement conservée)", d.qVides],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const ech = [
            ["N° règlement", "Émission chèque avant", "Émission chèque après", "Date règlement avant", "Date règlement après"],
            ...(d.echantillon || []).map(e => [e.numeroReglement, e.dateEmissionAvant, e.dateEmissionApres,
                e.dateReglementAvant, e.dateReglementApres]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ech), "Échantillon");
        const err = [["Détail"], ...(d.lignesErreur || []).map(x => [x])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(err), "Introuvables");
        XLSX.writeFile(wb, `correction_dates_gumvt_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setFichier(null); setApercu(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    const colsEch = [
        { title: "N° règl.", dataIndex: "numeroReglement", key: "numeroReglement", width: 90, align: "center",
          render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: "Émission chèque (col. D)", key: "emission", align: "center",
          render: (_, r) => <Space size={4}><Text delete type="secondary" style={{ fontSize: 11 }}>{r.dateEmissionAvant || "—"}</Text>
                <Text type="success" strong style={{ fontSize: 12 }}>{r.dateEmissionApres || "—"}</Text></Space> },
        { title: "Date règlement (col. Q)", key: "reglement", align: "center",
          render: (_, r) => <Space size={4}><Text delete type="secondary" style={{ fontSize: 11 }}>{r.dateReglementAvant || "—"}</Text>
                <Text type="success" strong style={{ fontSize: 12 }}>{r.dateReglementApres || "—"}</Text></Space> },
    ];

    const Indics = ({ d }) => (
        <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Décaissements corrigés" value={d.corriges}
                    valueStyle={{ color: "#52c41a", fontSize: 18 }} prefix={<CheckCircleFilled />} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Lignes traitées" value={d.totalLignes}
                    valueStyle={{ fontSize: 18, color: "#242166" }} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Ignorées (col. D vide)" value={d.ignoresDVide}
                    valueStyle={{ fontSize: 18, color: "#8c8c8c" }} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Col. Q vide" value={d.qVides}
                    valueStyle={{ fontSize: 18, color: d.qVides > 0 ? "#faad14" : "#8c8c8c" }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Introuvables" value={d.nonTrouves}
                    valueStyle={{ color: d.nonTrouves > 0 ? "#faad14" : "#52c41a", fontSize: 18 }} />
            </Col>
        </Row>
    );

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <CalendarOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Corriger les dates des règlements — Pool GUM / VT
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Émission chèque ← colonne D · date de règlement ← colonne Q (par n° de règlement)
                        </Text>
                    </div>
                </Space>

                <Steps current={step} style={{ marginBottom: 32 }} items={[
                    { title: "Sélection" }, { title: "Aperçu" }, { title: "Application" }, { title: "Rapport" },
                ]} />

                {step === 0 && (
                    <Card>
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="Lit la feuille « Règlements ». Pour chaque ligne dont la colonne D (DateRèg) est renseignée, retrouve le décaissement par son numéro (colonne A) et pose : date d'émission du chèque = colonne D, date de règlement = colonne Q (DateOD). Les lignes dont la colonne D est vide sont ignorées. Seules les dates changent (aucun montant). L'aperçu ne modifie rien." />
                        <Dragger accept=".xlsx,.xls" multiple={false}
                            beforeUpload={(f) => { setFichier(f); return false; }}
                            onRemove={() => setFichier(null)} maxCount={1} style={{ marginBottom: 24 }}>
                            <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: "#242166" }} /></p>
                            <p className="ant-upload-text">Cliquez ou déposez le fichier Excel Pool (sinistres)</p>
                            <p className="ant-upload-hint">Le même fichier que la reprise des sinistres GUM/VT</p>
                        </Dragger>
                        {fichier && (
                            <Alert type="success" showIcon style={{ marginBottom: 16 }}
                                message={<Space><FileExcelOutlined style={{ color: "#217346" }} />
                                    <Text strong>{fichier.name}</Text>
                                    <Text type="secondary">({(fichier.size / 1024).toFixed(1)} Ko)</Text></Space>} />
                        )}
                        <div style={{ textAlign: "right" }}>
                            <Button type="primary" size="large" icon={<EyeOutlined />} loading={loading}
                                disabled={!fichier} onClick={previsualiser} style={{ background: "#242166" }}>
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && apercu && (
                    <div>
                        <Alert type="warning" showIcon style={{ marginBottom: 20 }}
                            message="Aperçu — aucune écriture effectuée"
                            description={`${apercu.corriges} décaissement(s) seront corrigés. ${apercu.ignoresDVide} ligne(s) ignorée(s) (colonne D vide)${apercu.qVides > 0 ? `, ${apercu.qVides} ligne(s) avec colonne Q vide (date de règlement existante conservée)` : ""}. Vérifiez l'échantillon puis appliquez.`} />
                        <Card style={{ marginBottom: 20 }}><Indics d={apercu} /></Card>
                        {apercu.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({apercu.echantillon.length})</Text>}>
                                <Table dataSource={apercu.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        {apercu.lignesErreur?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Space><WarningOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Introuvables ({apercu.lignesErreur.length})</Text></Space>}>
                                <Table dataSource={apercu.lignesErreur.map((m, i) => ({ key: i, message: m }))}
                                    columns={[{ title: "Détail", dataIndex: "message", key: "message",
                                        render: v => <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> }]}
                                    size="small" pagination={{ pageSize: 15 }} scroll={{ y: 220 }} />
                            </Card>
                        )}
                        <Space>
                            <Button onClick={reinitialiser}>Annuler</Button>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(apercu)}>Exporter l'aperçu</Button>
                            <Button type="primary" icon={<ThunderboltOutlined />} disabled={apercu.corriges === 0}
                                onClick={appliquer} style={{ background: "#242166" }}>
                                Appliquer en base ({apercu.corriges})
                            </Button>
                        </Space>
                    </div>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}><Text strong style={{ fontSize: 16 }}>Correction en cours…</Text></div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Job ID : <Text code>{jobId}</Text></Text>
                                            <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginLeft: 8 }}>
                                                {ETAT_LABELS[jobEtat] || jobEtat}</Tag>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 3 && rapport && (
                    <div>
                        <Alert type="success" showIcon icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                            message={`${rapport.corriges} décaissement(s) corrigés (dates)`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s — sauvegarde réversible dans m_decaissement_bak_dates`} style={{ marginBottom: 20 }} />
                        <Card style={{ marginBottom: 20 }}><Indics d={rapport} /></Card>
                        {rapport.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({rapport.echantillon.length})</Text>}>
                                <Table dataSource={rapport.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(rapport)}>Exporter le rapport</Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser} style={{ background: "#242166" }}>
                                Nouvelle correction
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
